import { useState, useEffect } from 'react';
import { RelatedPerson } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { formatHumanName } from '@medplum/core';
import { HL7 } from '@consts/systems.consts';

export interface PatientRelatedPersonsResult {
  fatherName: string;
  motherName: string;
  spouseName: string;
  loading: boolean;
  relatedPersons: RelatedPerson[];
}

/**
 * Custom hook to fetch and parse RelatedPerson data for a patient
 * Automatically extracts and formats names for father, mother, and spouse
 *
 * @param patientId - The ID of the patient
 * @returns Object containing formatted parent/spouse names and loading state
 */
export function usePatientRelatedPersons(
  patientId: string | undefined
): PatientRelatedPersonsResult {
  const medplum = useMedplum();
  const [fatherName, setFatherName] = useState<string>('');
  const [motherName, setMotherName] = useState<string>('');
  const [spouseName, setSpouseName] = useState<string>('');
  const [relatedPersons, setRelatedPersons] = useState<RelatedPerson[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!patientId) {
      // Reset state if no patient ID
      setFatherName('');
      setMotherName('');
      setSpouseName('');
      setRelatedPersons([]);
      return;
    }

    setLoading(true);

    medplum
      .searchResources('RelatedPerson', {
        patient: `Patient/${patientId}`,
        _count: '20',
      })
      .then((persons: RelatedPerson[]) => {
        setRelatedPersons(persons);

        // Find father
        const father = persons.find(
          (rp) =>
            rp.relationship?.[0]?.coding?.[0]?.code ===
            HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.FATHER
        );
        if (father?.name?.[0]) {
          setFatherName(formatHumanName(father.name[0]));
        } else {
          setFatherName('');
        }

        // Find mother
        const mother = persons.find(
          (rp) =>
            rp.relationship?.[0]?.coding?.[0]?.code ===
            HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.MOTHER
        );
        if (mother?.name?.[0]) {
          setMotherName(formatHumanName(mother.name[0]));
        } else {
          setMotherName('');
        }

        // Find spouse
        const spouse = persons.find(
          (rp) =>
            rp.relationship?.[0]?.coding?.[0]?.code ===
            HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.SPOUSE
        );
        if (spouse?.name?.[0]) {
          setSpouseName(formatHumanName(spouse.name[0]));
        } else {
          setSpouseName('');
        }
      })
      .catch((err: unknown) => {
        console.error('Error fetching related persons:', err);
        // Reset on error
        setFatherName('');
        setMotherName('');
        setSpouseName('');
        setRelatedPersons([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [patientId, medplum]);

  return {
    fatherName,
    motherName,
    spouseName,
    loading,
    relatedPersons,
  };
}
